export const NYC_BOUNDS = {
  west: -74.2591,
  south: 40.4774,
  east: -73.7002,
  north: 40.9176,
} as const

export type VenueType = 'coffee' | 'brunch' | 'drinks'
export type RoadProfile = 'car' | 'bike' | 'foot'

export interface RoadPoint {
  lat: number
  lon: number
}

export function bikeSharePoints(value: string | null): RoadPoint[] | null {
  if (!value) return null
  const points = value.split(';')
  if (points.length < 1 || points.length > 31) return null
  const parsed = points.map((point): RoadPoint | null => {
    const [latitude, longitude, ...rest] = point.split(',')
    if (rest.length) return null
    const lat = coordinate(latitude ?? null, 'lat')
    const lon = coordinate(longitude ?? null, 'lon')
    return lat === null || lon === null ? null : { lat, lon }
  })
  return parsed.every((point) => point !== null) ? parsed as RoadPoint[] : null
}

export function searchQuery(value: string | null): string | null {
  const query = value?.trim().replaceAll(/\s+/g, ' ')
  return query && query.length >= 3 && query.length <= 200 ? query : null
}

export function coordinate(value: string | null, axis: 'lat' | 'lon'): number | null {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) {
    return null
  }

  const parsed = Number(value)
  const [minimum, maximum] = axis === 'lat'
    ? [NYC_BOUNDS.south, NYC_BOUNDS.north]
    : [NYC_BOUNDS.west, NYC_BOUNDS.east]

  return parsed >= minimum && parsed <= maximum ? parsed : null
}

export function venueType(value: string | null): VenueType | null {
  return value === 'coffee' || value === 'brunch' || value === 'drinks' ? value : null
}

export function radius(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null
  }

  const parsed = Number(value)
  return parsed >= 200 && parsed <= 1_500 ? parsed : null
}

export function searchCenters(value: string | null): Array<{ lat: number; lon: number }> | null {
  if (!value) return null
  const values = value.split(';')
  if (values.length < 1 || values.length > 3) return null
  const parsed = values.map((center) => {
    const [latitude, longitude, ...rest] = center.split(',')
    if (rest.length) return null
    const lat = coordinate(latitude ?? null, 'lat')
    const lon = coordinate(longitude ?? null, 'lon')
    return lat === null || lon === null ? null : { lat, lon }
  })
  return parsed.every((center) => center !== null)
    ? parsed as Array<{ lat: number; lon: number }>
    : null
}

export function roadRequest(value: unknown): {
  profile: RoadProfile
  origins: RoadPoint[]
  destinations: RoadPoint[]
} | null {
  if (!value || typeof value !== 'object') return null
  const request = value as Record<string, unknown>
  if (request.profile !== 'car' && request.profile !== 'bike' && request.profile !== 'foot') return null
  const isFoot = request.profile === 'foot'
  const origins = roadPoints(request.origins, 1, isFoot ? 40 : 6)
  const destinations = roadPoints(request.destinations, 1, isFoot ? 40 : 25)
  if (isFoot && origins && destinations
    && (origins.length + destinations.length > 100
      || origins.length * destinations.length > 2_500)) return null
  return origins && destinations ? { profile: request.profile, origins, destinations } : null
}

export function roadRouteRequest(value: unknown): {
  profile: RoadProfile
  origin: RoadPoint
  destination: RoadPoint
} | null {
  if (!value || typeof value !== 'object') return null
  const request = value as Record<string, unknown>
  if (request.operation !== 'route'
    || request.profile !== 'car' && request.profile !== 'bike' && request.profile !== 'foot') return null
  const origins = roadPoints([request.origin], 1, 1)
  const destinations = roadPoints([request.destination], 1, 1)
  return origins && destinations
    ? { profile: request.profile, origin: origins[0], destination: destinations[0] }
    : null
}

function roadPoints(value: unknown, minimum: number, maximum: number): RoadPoint[] | null {
  if (!Array.isArray(value) || value.length < minimum || value.length > maximum) return null
  const points = value.map((point): RoadPoint | null => {
    if (!point || typeof point !== 'object') return null
    const candidate = point as Record<string, unknown>
    if (typeof candidate.lat !== 'number' || typeof candidate.lon !== 'number') return null
    const lat = coordinate(candidate.lat.toString(), 'lat')
    const lon = coordinate(candidate.lon.toString(), 'lon')
    return lat === null || lon === null ? null : { lat, lon }
  })
  return points.every((point) => point !== null) ? points as RoadPoint[] : null
}
